// Catálogo "conhecido" de feature flags que o código REALMENTE usa via
// `flagEnabled("key")`. Admin não precisa decorar nomes — escolhe do dropdown
// e o sistema cria a flag com label/descrição/platform corretos.
//
// REGRA DE OURO: toda vez que o frontend adicionar um `flagEnabled("nova.key")`
// novo, adiciona uma entrada AQUI também. Sem isso, o admin volta a precisar
// decorar nomes.
//
// Pra descobrir flags faltando no catálogo:
//   grep -rohE 'flagEnabled\("[a-z._]+"' apps/ | sort -u
//
// [[project-feature-flag-admin-revamp]]

import type { FeatureFlagPlatform, FeatureFlagStatus } from "./api";

export interface KnownFlag {
  /** A chave técnica que o `flagEnabled(...)` usa no código. */
  key: string;
  /** Rótulo amigável que o admin vê na UI. */
  label: string;
  /** Explicação: o que essa flag liga/desliga, onde aparece, qual o efeito. */
  description: string;
  /** Plataforma alvo. `any` quando a flag é usada em mais de um portal. */
  platform: FeatureFlagPlatform;
  /** Status de maturidade default ao criar pela primeira vez. */
  defaultStatus: FeatureFlagStatus;
  /** Categoria pra ícone + filtro. Reaproveita as áreas de getArea(). */
  area:
    | "ai"
    | "recorder"
    | "notes"
    | "bancos"
    | "menu"
    | "share"
    | "payments"
    | "notif"
    | "msg"
    | "games"
    | "admin"
    | "geral";
}

export const KNOWN_FLAGS: KnownFlag[] = [
  // ===== Editor de questões (Manager + Teacher) =====
  {
    key: "question_editor.wysiwyg",
    label: "Editor visual de questões (WYSIWYG)",
    description:
      "Troca o textarea por contentEditable com toolbar (negrito/cor/listas/math/imagem) no editor de banco no /manager (VisualBankEditor) e /teacher (QuestionEditorView). Renderer aceita ![alt](data:image/...) inline + clique pra recortar.",
    platform: "any",
    defaultStatus: "beta",
    area: "bancos"
  },
  // ===== Sidebar do aluno =====
  {
    key: "student.menu.aistudy",
    label: "Aluno: item 'Estudar com IA' na sidebar",
    description:
      "Mostra/esconde o item 'Estudar com IA' (acesso ao AiStudyView) no menu lateral do portal do aluno.",
    platform: "student",
    defaultStatus: "stable",
    area: "ai"
  },
  {
    key: "student.menu.gravador",
    label: "Aluno: item 'Gravador de Aulas' na sidebar",
    description:
      "Mostra/esconde o item 'Gravador de Aulas' no menu lateral do portal do aluno.",
    platform: "student",
    defaultStatus: "experimental",
    area: "recorder"
  },
  {
    key: "student.menu.shares",
    label: "Aluno: item 'Compartilhamentos' na sidebar",
    description:
      "Mostra/esconde o item 'Compartilhamentos' (anotações/cadernos compartilhados com o aluno) no menu lateral.",
    platform: "student",
    defaultStatus: "beta",
    area: "share"
  },
  {
    key: "student.menu.disciplinas",
    label: "Aluno: item 'Disciplinas' na sidebar",
    description:
      "Mostra/esconde o item 'Disciplinas' no menu lateral do portal do aluno. Quando off/allowlist, só os listados veem o item — os demais ficam só com bancos, IA e essenciais.",
    platform: "student",
    defaultStatus: "stable",
    area: "menu"
  },
  {
    key: "student.menu.provas",
    label: "Aluno: item 'Provas' na sidebar",
    description:
      "Mostra/esconde o item 'Provas' no menu lateral do portal do aluno (lista de provas agendadas + badge de upcomingCount).",
    platform: "student",
    defaultStatus: "stable",
    area: "menu"
  },
  {
    key: "student.menu.agenda",
    label: "Aluno: item 'Agenda' na sidebar",
    description:
      "Mostra/esconde o item 'Agenda' no menu lateral do portal do aluno (calendário de eventos/aulas).",
    platform: "student",
    defaultStatus: "stable",
    area: "menu"
  },
  {
    key: "student.menu.inbox",
    label: "Aluno: item 'Caixa de entrada' na sidebar",
    description:
      "Mostra/esconde o item 'Caixa de entrada' no menu lateral do portal do aluno (notificações + mensagens da administração). Esconder remove o item mas as views ainda são acessíveis por deeplink.",
    platform: "student",
    defaultStatus: "stable",
    area: "menu"
  },
  {
    key: "student.menu.pagamentos",
    label: "Aluno: item 'Pagamentos e consumo' na sidebar",
    description:
      "Mostra/esconde o item 'Pagamentos e consumo' (PagamentosView com abas Plano/Cartões/Gastos IA/Assistente) no menu lateral do portal do aluno. Esconder remove o item da sidebar — abas ainda acessíveis via deeplink.",
    platform: "student",
    defaultStatus: "stable",
    area: "payments"
  },
  // ===== Anotações do Caderno =====
  {
    key: "student.notes.inline_ai",
    label: "Aluno: bolha de IA nas anotações (Explique/Resumir/Melhorar)",
    description:
      "Quando o aluno seleciona texto numa anotação do Caderno, mostra bolha flutuante com botões 'Explique', 'Resumir', 'Melhorar' que chamam a IA. Default OFF — custo de IA + decisão comercial.",
    platform: "student",
    defaultStatus: "beta",
    area: "ai"
  },
  {
    key: "student.notes.flashcards",
    label: "Aluno: botão 'Gerar flashcards' nas anotações",
    description:
      "Habilita botão de gerar flashcards a partir de trecho selecionado na anotação. Mais caro que Explique/Resumir.",
    platform: "student",
    defaultStatus: "experimental",
    area: "notes"
  }
];

/** Busca uma flag no catálogo pela key. */
export function findKnownFlag(key: string): KnownFlag | undefined {
  return KNOWN_FLAGS.find((f) => f.key === key);
}

/** Retorna apenas flags que NÃO estão cadastradas ainda no DB. */
export function knownFlagsNotYetCreated(existingKeys: string[]): KnownFlag[] {
  const set = new Set(existingKeys);
  return KNOWN_FLAGS.filter((f) => !set.has(f.key));
}
